// src/modules/purityAnalyzer/pages/TestResult.jsx
// Saved test result — the full instrument view with XRF verification,
// reversal, and audit trail.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Space, Typography, Tag, Modal, Form, InputNumber, Input,
  message, Spin, Descriptions, Timeline, Drawer, Alert,
} from 'antd';
import {
  ArrowLeftOutlined, SafetyCertificateOutlined, HistoryOutlined,
  UndoOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ResultInstrument from '../components/ResultInstrument';
import { fetchTest, fetchTestAudit, verifyTestXrf, reverseTest } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import { XRF_STATUS_COLORS } from '../constants/purityConstants';

const { Title, Text } = Typography;

export default function TestResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xrfOpen, setXrfOpen] = useState(false);
  const [xrfForm] = Form.useForm();
  const [xrfSaving, setXrfSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTest(id);
      setTest(res?.data?.data || null);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load test');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ?xrf=1 (from the XRF Verification queue) auto-opens the XRF entry modal.
  useEffect(() => {
    if (searchParams.get('xrf') !== '1' || !test) return;
    if (can('purityAnalyzer', 'verifyXrf') && test.xrfStatus === 'PENDING' && test.status !== 'REVERSED') {
      setXrfOpen(true);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test]);

  const openAudit = async () => {
    setAuditOpen(true);
    try {
      const res = await fetchTestAudit(id);
      setAuditRows(res?.data?.data || []);
    } catch { setAuditRows([]); }
  };

  const submitXrf = async () => {
    try {
      const values = await xrfForm.validateFields();
      setXrfSaving(true);
      const res = await verifyTestXrf(id, values);
      const acc = res?.data?.data?.accuracy;
      message.success(
        `XRF recorded — absolute error ${acc?.absoluteError}% (relative ${acc?.relativeError}%)`
      );
      setXrfOpen(false);
      xrfForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'XRF verification failed');
    } finally {
      setXrfSaving(false);
    }
  };

  const onReverse = async () => {
    try {
      const { reason } = await reverseForm.validateFields();
      await reverseTest(id, reason.trim());
      message.success('Test reversed (record retained)');
      setReverseOpen(false);
      reverseForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Reversal failed');
    }
  };

  if (loading && !test) {
    return <div style={{ padding: 80, textAlign: 'center' }}><Spin size="large" /></div>;
  }
  if (!test) return null;

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/purity/tests')}>
            Test History
          </Button>
          <Title level={4} style={{ margin: 0 }}>{test.testNumber}</Title>
          <Tag color={XRF_STATUS_COLORS[test.xrfStatus]}>XRF {test.xrfStatus}</Tag>
        </Space>
        <Space wrap>
          <Button icon={<HistoryOutlined />} onClick={openAudit}>Audit Trail</Button>
          {can('purityAnalyzer', 'verifyXrf') && test.xrfStatus === 'PENDING' && test.status !== 'REVERSED' && (
            <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => setXrfOpen(true)}>
              Verify by XRF
            </Button>
          )}
          {can('purityAnalyzer', 'runTest') && test.status !== 'REVERSED' && (
            <Button danger icon={<UndoOutlined />} onClick={() => setReverseOpen(true)}>
              Reverse
            </Button>
          )}
        </Space>
      </Space>

      {test.status === 'REVERSED' && (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }}
          message={`Reversed ${test.reversedAt ? dayjs(test.reversedAt).format('DD MMM YYYY HH:mm') : ''}`}
          description={test.reverseReason}
        />
      )}

      <ResultInstrument result={test} context={test} animateKey={test._id} />

      {/* ── XRF outcome ── */}
      {test.xrfStatus === 'VERIFIED' && test.xrfResult && (
        <Card
          size="small" style={{ marginTop: 12 }}
          title={<span><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />XRF Verification</span>}
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small">
            <Descriptions.Item label="XRF Purity">
              <Text strong>{test.xrfResult.xrfPurity}%</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Predicted">{test.predictedPurity}%</Descriptions.Item>
            <Descriptions.Item label="Absolute Error">{test.absoluteError}%</Descriptions.Item>
            <Descriptions.Item label="Relative Error">{test.relativeError}%</Descriptions.Item>
            <Descriptions.Item label="XRF Machine">{test.xrfResult.xrfMachine || '—'}</Descriptions.Item>
            <Descriptions.Item label="Verified At">
              {dayjs(test.xrfResult.verifiedAt).format('DD MMM YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Number">{test.xrfResult.xrfNumber}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* ── XRF modal ── */}
      <Modal
        title="Verify by XRF"
        open={xrfOpen}
        onCancel={() => setXrfOpen(false)}
        onOk={submitXrf}
        confirmLoading={xrfSaving}
        okText="Record XRF Result"
      >
        <Form form={xrfForm} layout="vertical">
          <Form.Item
            name="xrfPurity" label="XRF Purity (%)"
            rules={[{ required: true, message: 'Enter the XRF purity' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0.01} max={100} step={0.01} precision={2} />
          </Form.Item>
          <Form.Item name="xrfMachine" label="XRF Machine">
            <Input placeholder="Device name / serial" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Reverse modal ── */}
      <Modal
        title="Reverse Test"
        open={reverseOpen}
        onCancel={() => setReverseOpen(false)}
        onOk={onReverse}
        okText="Reverse Test"
        okButtonProps={{ danger: true }}
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="The result is never deleted — it stays on record with status REVERSED." />
        <Form form={reverseForm} layout="vertical">
          <Form.Item name="reason" label="Reason"
            rules={[{ required: true, whitespace: true, message: 'A reason is required' }]}>
            <Input.TextArea rows={2} placeholder="Why is this test being reversed?" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Audit drawer ── */}
      <Drawer title={`Audit Trail — ${test.testNumber}`} open={auditOpen}
        onClose={() => setAuditOpen(false)} width={440}>
        <Timeline
          items={auditRows.map((row) => ({
            children: (
              <div>
                <Text strong>{row.action.replace(/_/g, ' ')}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.performedBy?.name || 'System'} · {dayjs(row.createdAt).format('DD MMM YYYY HH:mm:ss')}
                  </Text>
                </div>
              </div>
            ),
          }))}
        />
        {!auditRows.length && <Text type="secondary">No audit entries.</Text>}
      </Drawer>
    </div>
  );
}
